<template>
  <BaseCard class="flex flex-col gap-2 p-4 md:gap-4 md:p-6">
    <div
      class="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-start md:justify-between"
    >
      <div>
        <div class="flex items-center gap-1.5">
          <TextSubtitle> Indicatieve opbrengsten </TextSubtitle>
          <WikiTooltipComponent path="/nl/platform/insights/about" />
        </div>
        <TextDescription>
          Verwacht financieel resultaat en energie
        </TextDescription>
      </div>
    </div>
    <LoadingWrapper :loading :error="loadingFailed">
      <div v-if="hasData" class="h-87.5">
        <TimeseriesChart
          :chart-type="bucketSize === BUCKET.MINUTE ? 'step' : 'bar'"
          :series="series"
          :axes="axes"
          :data="chartData"
          :x-limits="chartLimits"
          :time-unit="bucketSize !== BUCKET.MINUTE ? bucketSize : null"
          aligned-axes
        />
      </div>
      <p v-else class="text-gray-500 italic">
        Geen resultaten beschikbaar voor deze selectie.
      </p>
    </LoadingWrapper>
    <slot name="extras" />
  </BaseCard>
</template>

<script setup>
import { computed } from "vue"
import { endOfDay, startOfMonth, startOfYear } from "date-fns"
import { BUCKET } from "@/helpers/filter.js"
import useResultsData from "@/composables/useResultsData.js"
import {
  BaseCard,
  TextSubtitle,
  TextDescription,
  LoadingWrapper,
  TimeseriesChart,
} from "@repowerednl/ui-component-library"
import WikiTooltipComponent from "@/components/information/WikiTooltipComponent.vue"

const props = defineProps({
  meteringPoint: { type: Object, default: null },
  dateRange: { type: Array, required: true },
})

const {
  recordsData,
  loading,
  loadingFailed,
  bucketSize,
  categorizeRecord,
  shouldSplitNomination,
} = useResultsData(
  () => props.meteringPoint,
  () => props.dateRange,
)

const chartLimits = computed(() => {
  switch (bucketSize.value) {
    case BUCKET.MINUTE:
      return [props.dateRange[0], endOfDay(props.dateRange[1])]
    case BUCKET.MONTH:
      return [
        startOfMonth(props.dateRange[0]),
        startOfMonth(props.dateRange[1]),
      ]
    case BUCKET.YEAR:
      return [startOfYear(props.dateRange[0]), startOfYear(props.dateRange[1])]
    default:
      return [props.dateRange[0], props.dateRange[1]]
  }
})

const axes = {
  finance: {
    title: "Resultaat",
    format: "€ _",
    precision: 0,
  },
  energy: {
    title: "Energie",
    unit: "Wh",
    minPrefix: "k",
    precision: 0,
  },
}

const series = computed(() => ({
  revenue: {
    label: "Resultaat",
    axis: "finance",
    color: "finance",
  },
  allocation: {
    label: "Energie",
    axis: "energy",
    rescale: 1000,
    color: "energy",
  },
  ...(shouldSplitNomination.value
    ? {
        nomination: {
          label: "Nominatie na intraday",
          axis: "energy",
          rescale: 1000,
          color: "gray",
          overlay:
            bucketSize.value === BUCKET.MINUTE ? undefined : "positionDayAhead",
          legendOrder: 2,
        },
      }
    : {}),
  positionDayAhead: {
    label: shouldSplitNomination.value ? "Positie dagmarkt" : "Nominatie",
    axis: "energy",
    rescale: 1000,
    color: "lightgray",
    legendOrder: 1,
  },
}))

const chartData = computed(() => {
  if (!recordsData.value) {
    return {}
  }
  const result = {}
  for (const [datetime, { data }] of Object.entries(
    recordsData.value.records,
  )) {
    result[datetime] = categorizeRecord(data, {
      typeFilter: (spec) => !spec.is_hidden_insights,
    })
  }
  return result
})

const hasData = computed(() => Object.keys(chartData.value).length > 0)
</script>
